/**
 * Staff Notification Service
 * 
 * Handles multi-channel notifications for staff management:
 * - In-app notifications (stored in user's subcollection)
 * - SMS notifications (queued via messages collection)
 * - Email notifications (queued for Cloud Function processing)
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';

// ============================================================================
// CONSTANTS
// ============================================================================

export const NOTIFICATION_TYPES = {
  // Designation notifications
  DESIGNATION_RECEIVED: 'designation_received',
  DESIGNATION_ACCEPTED: 'designation_accepted',
  DESIGNATION_DECLINED: 'designation_declined',
  DESIGNATION_REVOKED: 'designation_revoked',
  DESIGNATION_EXPIRED: 'designation_expired',
  
  // Join request notifications
  JOIN_REQUEST_RECEIVED: 'join_request_received',
  JOIN_REQUEST_APPROVED: 'join_request_approved',
  JOIN_REQUEST_DENIED: 'join_request_denied',
  
  // General staff notifications
  STAFF_REMOVED: 'staff_removed',
  ROLE_UPDATED: 'role_updated',
  LOTSOG_DISCUSSION_REPLY: 'lotsog_discussion_reply',
};

export const NOTIFICATION_CHANNELS = {
  IN_APP: 'inApp',
  SMS: 'sms',
  EMAIL: 'email',
};

// ============================================================================
// IN-APP NOTIFICATION FUNCTIONS
// ============================================================================

/**
 * Create an in-app notification for a user
 * @param {string} uid - Target user ID
 * @param {Object} notification - Notification data
 * @returns {Promise<string>} Notification ID
 */
export async function createInAppNotification(uid, notification) {
  if (!uid) {
    throw new Error('User ID is required');
  }
  
  const {
    type,
    title,
    message,
    actionUrl,
    metadata,
  } = notification;
  
  if (!type || !title || !message) {
    throw new Error('Type, title, and message are required');
  }
  
  const notificationDoc = {
    type,
    title,
    message,
    actionUrl: actionUrl || null,
    metadata: metadata || {},
    read: false,
    createdAt: firebase.firestore.Timestamp.now(),
  };
  
  const docRef = await db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .add(notificationDoc);
  
  return docRef.id;
}

/**
 * Get unread notifications for a user
 * @param {string} uid - User ID
 * @param {number} limit - Maximum number of notifications to return
 * @returns {Promise<Array>} Array of notifications
 */
export async function getUnreadNotifications(uid, limit = 20) {
  if (!uid) {
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('users')
      .doc(uid)
      .collection('notifications')
      .where('read', '==', false)
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffNotification] Error fetching unread notifications:', error);
    return [];
  }
}

/**
 * Get all notifications for a user
 * @param {string} uid - User ID
 * @param {number} limit - Maximum number of notifications to return
 * @returns {Promise<Array>} Array of notifications
 */
export async function getNotifications(uid, limit = 50) {
  if (!uid) {
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('users')
      .doc(uid)
      .collection('notifications')
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffNotification] Error fetching notifications:', error);
    return [];
  }
}

/**
 * Get unread notification count for a user
 * @param {string} uid - User ID
 * @returns {Promise<number>} Unread count
 */
export async function getUnreadNotificationCount(uid) {
  if (!uid) {
    return 0;
  }
  
  try {
    const snapshot = await db
      .collection('users')
      .doc(uid)
      .collection('notifications')
      .where('read', '==', false)
      .get();
    
    return snapshot.size;
  } catch (error) {
    console.error('[StaffNotification] Error counting unread notifications:', error);
    return 0;
  }
}

/**
 * Mark a notification as read
 * @param {string} uid - User ID
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export async function markNotificationRead(uid, notificationId) {
  if (!uid || !notificationId) {
    return;
  }
  
  await db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .doc(notificationId)
    .update({
      read: true,
      readAt: firebase.firestore.Timestamp.now(),
    });
  
}

/**
 * Mark all notifications as read for a user
 * @param {string} uid - User ID
 * @returns {Promise<void>}
 */
export async function markAllNotificationsRead(uid) {
  if (!uid) {
    return;
  }
  
  const snapshot = await db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .where('read', '==', false)
    .get();
  
  if (snapshot.empty) {
    return;
  }
  
  const batch = db.batch();
  const now = firebase.firestore.Timestamp.now();
  
  snapshot.docs.forEach(doc => {
    batch.update(doc.ref, {
      read: true,
      readAt: now,
    });
  });
  
  await batch.commit();
  
}

/**
 * Delete old notifications (cleanup)
 * @param {string} uid - User ID
 * @param {number} daysOld - Delete notifications older than this many days
 * @returns {Promise<number>} Number of deleted notifications
 */
export async function deleteOldNotifications(uid, daysOld = 90) {
  if (!uid) {
    return 0;
  }
  
  const cutoffDate = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000);
  const cutoffTimestamp = firebase.firestore.Timestamp.fromDate(cutoffDate);
  
  const snapshot = await db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .where('createdAt', '<', cutoffTimestamp)
    .get();
  
  if (snapshot.empty) {
    return 0;
  }
  
  const batch = db.batch();
  snapshot.docs.forEach(doc => {
    batch.delete(doc.ref);
  });
  
  await batch.commit();
  
  return snapshot.size;
}

// ============================================================================
// SMS NOTIFICATION FUNCTIONS
// ============================================================================

/**
 * Queue an SMS notification
 * @param {string} phone - Target phone number
 * @param {string} message - SMS message body
 * @param {string} type - Notification type
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<string>} Message ID
 */
export async function queueSmsNotification(phone, message, type, metadata = {}) {
  if (!phone) {
    console.warn('[StaffNotification] No phone number provided for SMS');
    return null;
  }
  
  if (!message) {
    throw new Error('Message is required for SMS notification');
  }
  
  const smsDoc = {
    to: phone,
    body: message,
    type: type || 'staff_notification',
    status: 'pending',
    metadata,
    createdAt: firebase.firestore.Timestamp.now(),
  };
  
  const docRef = await db.collection('messages').add(smsDoc);
  
  return docRef.id;
}

// ============================================================================
// EMAIL NOTIFICATION FUNCTIONS
// ============================================================================

/**
 * Queue an email notification
 * @param {string} email - Target email address
 * @param {string} templateId - Email template ID
 * @param {Object} data - Template data
 * @returns {Promise<string>} Email queue ID
 */
export async function queueEmailNotification(email, templateId, data = {}) {
  if (!email) {
    console.warn('[StaffNotification] No email provided for notification');
    return null;
  }
  
  if (!templateId) {
    throw new Error('Template ID is required for email notification');
  }
  
  const emailDoc = {
    to: email.toLowerCase(),
    templateId,
    data,
    status: 'pending',
    createdAt: firebase.firestore.Timestamp.now(),
  };
  
  const docRef = await db.collection('emailQueue').add(emailDoc);
  
  return docRef.id;
}

// ============================================================================
// ORCHESTRATION FUNCTIONS
// ============================================================================

/**
 * Send all notifications for a staff designation
 * @param {Object} designation - Designation data
 * @param {Object} designator - Designator user data
 * @param {Object} facility - Facility data
 * @returns {Promise<Object>} Results of notification attempts
 */
export async function sendDesignationNotifications(designation, designator, facility) {
  const results = {
    inApp: false,
    sms: false,
    email: false,
    errors: [],
  };
  
  const facilityName = facility?.name || 'a healthcare facility';
  const designatorName = designator?.name ? 
    `${designator.name} ${designator.lastName || ''}`.trim() : 
    'A supervisor';
  
  // Try to find existing user by email for in-app notification
  try {
    const existingUserSnapshot = await db
      .collection('users')
      .where('email', '==', designation.email)
      .limit(1)
      .get();
    
    if (!existingUserSnapshot.empty) {
      const existingUser = existingUserSnapshot.docs[0];
      
      await createInAppNotification(existingUser.id, {
        type: NOTIFICATION_TYPES.DESIGNATION_RECEIVED,
        title: 'Staff Invitation',
        message: `${designatorName} has invited you to join ${facilityName} as ${formatRole(designation.designatedRole)}.`,
        actionUrl: `/join/d_${designation.id}`,
        metadata: {
          designationId: designation.id,
          facilityId: designation.facilityId,
          designatorId: designator?.uid,
        },
      });
      
      results.inApp = true;
    }
  } catch (error) {
    console.error('[StaffNotification] Error sending in-app notification:', error);
    results.errors.push({ channel: 'inApp', error: error.message });
  }
  
  // Send SMS if phone provided
  if (designation.phone) {
    try {
      const smsMessage = `You've been invited to join ${facilityName} on HealthDesk. ` +
        `Accept at: ${getAcceptUrl(designation.id)}`;
      
      await queueSmsNotification(
        designation.phone,
        smsMessage,
        'staff_designation',
        { designationId: designation.id }
      );
      
      results.sms = true;
    } catch (error) {
      console.error('[StaffNotification] Error queueing SMS:', error);
      results.errors.push({ channel: 'sms', error: error.message });
    }
  }
  
  // Queue email notification
  try {
    await queueEmailNotification(designation.email, 'staff_designation', {
      designationId: designation.id,
      recipientName: designation.name,
      recipientEmail: designation.email,
      facilityName,
      designatorName,
      role: formatRole(designation.designatedRole),
      acceptUrl: getAcceptUrl(designation.id),
      expiresAt: designation.expiresAt?.toDate?.()?.toISOString() || null,
    });
    
    results.email = true;
  } catch (error) {
    console.error('[StaffNotification] Error queueing email:', error);
    results.errors.push({ channel: 'email', error: error.message });
  }
  
  // Update designation with notification status
  await db.collection('staffDesignations').doc(designation.id).update({
    notificationsSent: {
      inApp: results.inApp,
      sms: results.sms,
      email: results.email,
    },
    notificationsSentAt: firebase.firestore.Timestamp.now(),
  });
  
  return results;
}

/**
 * Send notifications for a join request to supervisors
 * @param {Object} request - Join request data
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Results of notification attempts
 */
export async function sendJoinRequestNotifications(request, facilityId) {
  const results = {
    notifiedSupervisors: 0,
    errors: [],
  };
  
  if (!facilityId) {
    return results;
  }
  
  try {
    // Find supervisors for this facility who can approve
    const supervisorRoles = ['facility', 'department-admin', 'chartmind-supervisor'];
    
    let supervisorQuery = db
      .collection('users')
      .where('facilityId', '==', facilityId)
      .where('role', 'in', supervisorRoles);
    
    // Scope to department if specified
    if (request.departmentId) {
      supervisorQuery = db
        .collection('users')
        .where('facilityId', '==', facilityId)
        .where('departmentId', '==', request.departmentId)
        .where('role', 'in', ['department-admin', 'chartmind-supervisor']);
    }
    
    const supervisorsSnapshot = await supervisorQuery.get();
    
    for (const doc of supervisorsSnapshot.docs) {
      try {
        await createInAppNotification(doc.id, {
          type: NOTIFICATION_TYPES.JOIN_REQUEST_RECEIVED,
          title: 'Join Request',
          message: `${request.requesterName} has requested to join as ${formatRole(request.requestedRole)}.`,
          actionUrl: '/dashboard/credential-management',
          metadata: {
            requestId: request.id,
            requesterId: request.requesterUid,
          },
        });
        
        results.notifiedSupervisors++;
      } catch (error) {
        results.errors.push({ supervisorId: doc.id, error: error.message });
      }
    }
  } catch (error) {
    console.error('[StaffNotification] Error sending join request notifications:', error);
    results.errors.push({ error: error.message });
  }
  
  return results;
}

/**
 * Notify user that their join request was approved
 * @param {Object} request - Join request data
 * @param {Object} approver - Approver user data
 * @returns {Promise<void>}
 */
export async function sendJoinRequestApprovedNotification(request, approver) {
  const approverName = approver?.name ? 
    `${approver.name} ${approver.lastName || ''}`.trim() : 
    'A supervisor';
  
  await createInAppNotification(request.requesterUid, {
    type: NOTIFICATION_TYPES.JOIN_REQUEST_APPROVED,
    title: 'Request Approved',
    message: `${approverName} has approved your request to join as ${formatRole(request.requestedRole)}.`,
    actionUrl: '/dashboard',
    metadata: {
      requestId: request.id,
      approverId: approver?.uid,
    },
  });
}

/**
 * Notify user that their join request was denied
 * @param {Object} request - Join request data
 * @param {string} reason - Denial reason
 * @returns {Promise<void>}
 */
export async function sendJoinRequestDeniedNotification(request, reason) {
  await createInAppNotification(request.requesterUid, {
    type: NOTIFICATION_TYPES.JOIN_REQUEST_DENIED,
    title: 'Request Denied',
    message: reason ? 
      `Your request to join has been denied: ${reason}` :
      'Your request to join has been denied.',
    actionUrl: null,
    metadata: {
      requestId: request.id,
    },
  });
}

/**
 * Notify designator that designation was accepted
 * @param {Object} designation - Designation data
 * @param {Object} acceptingUser - User who accepted
 * @returns {Promise<void>}
 */
export async function sendDesignationAcceptedNotification(designation, acceptingUser) {
  const accepterName = acceptingUser?.name ? 
    `${acceptingUser.name} ${acceptingUser.lastName || ''}`.trim() : 
    designation.name || designation.email;
  
  await createInAppNotification(designation.createdBy, {
    type: NOTIFICATION_TYPES.DESIGNATION_ACCEPTED,
    title: 'Designation Accepted',
    message: `${accepterName} has accepted your invitation to join as ${formatRole(designation.designatedRole)}.`,
    actionUrl: '/dashboard/credential-management',
    metadata: {
      designationId: designation.id,
      acceptedByUid: acceptingUser?.uid,
    },
  });
}

/**
 * Notify designator that designation was declined
 * @param {Object} designation - Designation data
 * @returns {Promise<void>}
 */
export async function sendDesignationDeclinedNotification(designation) {
  const declinedUserName = designation.name || designation.email;
  
  await createInAppNotification(designation.createdBy, {
    type: NOTIFICATION_TYPES.DESIGNATION_DECLINED,
    title: 'Designation Declined',
    message: `${declinedUserName} has declined your invitation to join.`,
    actionUrl: '/dashboard/credential-management',
    metadata: {
      designationId: designation.id,
    },
  });
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format role name for display
 * @param {string} role - Role identifier
 * @returns {string} Formatted role name
 */
function formatRole(role) {
  const roleNames = {
    'facility': 'Facility Administrator',
    'department-admin': 'Department Administrator',
    'chartmind-supervisor': 'Clinic Supervisor',
    'professional-chartmind': 'Healthcare Professional',
    'professional': 'Healthcare Professional',
  };
  
  return roleNames[role] || role;
}

/**
 * Get the accept URL for a designation
 * @param {string} designationId - Designation ID
 * @returns {string} Accept URL
 */
function getAcceptUrl(designationId) {
  // In production, this should use the actual domain
  const baseUrl = typeof window !== 'undefined' ? 
    window.location.origin : 
    'https://hlthdsk-experimental-demo.web.app';
  
  return `${baseUrl}/join/d_${designationId}`;
}

// ============================================================================
// REAL-TIME SUBSCRIPTION
// ============================================================================

/**
 * Subscribe to notification updates for a user
 * @param {string} uid - User ID
 * @param {Function} callback - Callback function for updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToNotifications(uid, callback) {
  if (!uid) {
    return () => {};
  }
  
  return db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .orderBy('createdAt', 'desc')
    .limit(20)
    .onSnapshot(
      snapshot => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(notifications);
      },
      error => {
        console.error('[StaffNotification] Subscription error:', error);
      }
    );
}

/**
 * Subscribe to unread notification count
 * @param {string} uid - User ID
 * @param {Function} callback - Callback function for count updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToUnreadCount(uid, callback) {
  if (!uid) {
    return () => {};
  }
  
  return db
    .collection('users')
    .doc(uid)
    .collection('notifications')
    .where('read', '==', false)
    .onSnapshot(
      snapshot => {
        callback(snapshot.size);
      },
      error => {
        console.error('[StaffNotification] Unread count subscription error:', error);
      }
    );
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Constants
  NOTIFICATION_TYPES,
  NOTIFICATION_CHANNELS,
  
  // In-app notification functions
  createInAppNotification,
  getUnreadNotifications,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteOldNotifications,
  
  // SMS notification functions
  queueSmsNotification,
  
  // Email notification functions
  queueEmailNotification,
  
  // Orchestration functions
  sendDesignationNotifications,
  sendJoinRequestNotifications,
  sendJoinRequestApprovedNotification,
  sendJoinRequestDeniedNotification,
  sendDesignationAcceptedNotification,
  sendDesignationDeclinedNotification,
  
  // Real-time subscriptions
  subscribeToNotifications,
  subscribeToUnreadCount,
};
